<template>
  <div class="menu-item" :class="itemClasses">
    <div 
      class="menu-item-content"
      @click="onClick"
      :class="{
        'has-submenu': hasSubmenus,
        'is-expanded': isExpanded,
      }"
    >
      <div class="menu-item-left">
        <div
          class="menu-icon"
          :class="iconColorClass"
          v-if="item.icon"
        >
          <v-icon :size="iconSize">{{ item.icon }}</v-icon>
        </div>

        <div class="menu-text">
          <span class="menu-title">{{ item.title }}</span>
          <span class="menu-subtitle" v-if="item.subtitle">{{ item.subtitle }}</span>
        </div>
      </div>

      <div class="menu-item-right">
        <div class="menu-badges">
          <div 
            class="badge badge-red"
            v-if="item.badge && item.badge > 0" 
          >
            {{ item.badge }}
          </div>

          <div 
            class="badge badge-alert"
            v-if="item.alert" 
          >
            !
          </div>

          <div 
            class="status-badge"
            :class="`status-${item.status}`"
            v-if="item.status" 
          >
            {{ item.statusText || item.status }}
          </div>

          <div 
            class="premium-badge"
            v-if="item.premium" 
          >
            <v-icon size="16">mdi-crown</v-icon>
            <span>Premium</span>
          </div>

          <div
            class="custom-badge"
            :class="[
              item.customBadge.color ? `badge-${item.customBadge.color}` : '',
              item.customBadge.outline ? 'badge-outline' : '',
            ]"
            v-if="item.customBadge"
          >
            {{ item.customBadge.label }}
          </div>
        </div>

        <div class="menu-actions">
          <v-icon 
            v-if="hasSubmenus" 
            size="16" 
            class="chevron"
            :class="{ 'expanded': isExpanded }"
          >
            mdi-chevron-down
          </v-icon>
        </div>
      </div>
    </div>

    <transition
      :name="skipAnimation ? '' : 'submenu'"
      @enter="onSubmenuEnter"
      @after-enter="onSubmenuAfterEnter"
      @leave="onSubmenuLeave"
      @after-leave="onSubmenuAfterLeave"
    >
      <div v-if="hasSubmenus && isExpanded" class="submenu">
        <menu-item
          :key="`sub-${index}`"
          :item="subItem"
          :level="level + 1"
          v-for="(subItem, index) in item.submenus"
        />
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'MenuItem',

  props: {    
    level: {
      type: Number,
      default: 0,
    },

    item: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      isExpanded: false,
      skipAnimation: false,
    };
  },

  mounted() {
    this.checkAndExpandIfActive();
  },

  computed: {
    bgColorClass() {
      if (this.item.color) {
        return `bg-${this.item.color}`;
      }
      
      return 'bg-default';
    },

    hasSubmenus() {
      return Array.isArray(this.item.submenus) && this.item.submenus.length > 0;
    },

    itemClasses() {
      return {
        [this.bgColorClass]: !!this.item.color,
        'alert': this.item.alert,
        'has-submenu': this.hasSubmenus,
        'is-expanded': this.isExpanded,
        'is-disabled': this.item.disabled,
        'is-active': this.item.active,
        [`level-${this.level}`]: true,
      };
    },

    iconSize() {
      return this.level === 0 ? 20 : 18;
    },

    iconColorClass() {
      if (this.item.color) {
        return `icon-${this.item.color}`;
      }
      
      if (this.item.alert) {
        return 'icon-red';
      }
      
      if (this.item.premium) {
        return 'icon-orange';
      }
      
      return 'icon-default';
    },
  },

  methods: {
    checkAndExpandIfActive() {
      if (this.hasSubmenus) {
        const checkSubmenu = (submenu) => {
          if (submenu.active) {
            return true;
          }

          if (submenu.submenus) {
            return submenu.submenus.some(checkSubmenu);
          }

          return false;
        };

        const shouldExpand = this.item.submenus.some(checkSubmenu);
        
        if (shouldExpand && !this.isExpanded) {
          this.skipAnimation = true;
          this.isExpanded = true;
          this.$nextTick(() => {
            this.skipAnimation = false;
          });
        } else {
          this.isExpanded = shouldExpand;
        }
      }
    },

    onClick() {
      if (this.item.disabled) {
        return;
      }

      if (this.hasSubmenus) {
        this.isExpanded = !this.isExpanded;
      } else if (this.item.onClick) {
        this.item.onClick();
      } else if (this.item.to) {
        this.$router.push({ name: this.item.to });
      } else if (this.item.href) {
        window.open(this.item.href, this.item.target || '_blank');
      }
    },

    onSubmenuEnter(el) {
      el.style.height = '0';
      el.style.overflow = 'hidden';
      el.offsetHeight;
      el.style.height = el.scrollHeight + 'px';
    },

    onSubmenuAfterEnter(el) {
      el.style.height = 'auto';
      el.style.overflow = 'visible';
    },

    onSubmenuLeave(el) {
      el.style.height = el.scrollHeight + 'px';
      el.style.overflow = 'hidden';
      el.offsetHeight;
      el.style.height = '0';
    },

    onSubmenuAfterLeave(el) {
      el.style.height = 'auto';
      el.style.overflow = 'visible';
    },
  },

  watch: {
    '$route'() {
      this.checkAndExpandIfActive();
    },

    'item.submenus'() {
      this.checkAndExpandIfActive();
    },
  },
};
</script>

<style lang="scss" scoped>
$blue: #3B82F6;
$default: #6B7280;
$red: #EF4444;
$orange: #F59E0B;
$green: #10B981;
$purple: #8b5CF6;

.menu-actions {
  display: flex;
  align-items: center;
}

.menu-item-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.menu-item-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    background-color: #f9fafb;
  }

  &.has-submenu {
    .menu-item-right {
      .menu-actions {
        .chevron {
          transition: transform 0.2s ease;
        }
      }
    }

    &.is-expanded {
      .menu-item-right {
        .menu-actions {
          .chevron {
            transform: rotate(180deg);
          }
        }
      }
    }
  }
}

.menu-text {
  flex: 1;
  min-width: 0;

  .menu-title {
    display: block;
    font-size: 16px;
    font-weight: 500;
    color: #111827;
    line-height: 1.4;
  }

  .menu-subtitle {
    display: block;
    font-size: 12px;
    color: #6b7280;
    line-height: 1.3;
    margin-top: 2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.menu-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;

  &.icon-default i {
    color: $default !important;
  }

  &.icon-blue i {
    color: $blue !important;
  }

  &.icon-green i {
    color: $green !important;
  }

  &.icon-purple i {
    color: $purple !important;
  }

  &.icon-orange i {
    color: $orange !important;
  }

  &.icon-red i {
    color: $red !important;
  }
}

.menu-item {
  @apply font-sans;

  &.level-1 .menu-item-content {
    padding-left: 44px;
  }

  &.level-2 .menu-item-content {
    padding-left: 80px;
  }

  &:not(.level-0) {
    .menu-title {
      font-weight: 400;
    }
  }

  &.is-disabled {
    opacity: 0.5;
    pointer-events: none;

    .menu-icon i {
      color: $default !important;
    }
  }

  &.is-active {
    &.bg-blue .menu-item-content {
      background-color: #EFF6FF;
    }

    &.bg-green .menu-item-content {
      background-color: #ECFDF5;
    }

    &.bg-purple .menu-item-content {
      background-color: #F3E8FF;
    }

    &.bg-orange .menu-item-content {
      background-color: #FFFBEB;
    }

    &.bg-red .menu-item-content {
      background-color: #FEF2F2;
    }
  }

  &.alert .menu-icon i {
    color: $red !important;
  }
}

.menu-item-left {
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 1;
  min-width: 0;
}

.menu-badges {
  display: flex;
  align-items: center;
  gap: 6px;
}

.badge {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
  color: white;

  &.badge-red {
    background-color: $red;
  }

  &.badge-alert {
    background-color: $red;
    min-width: 20px;
    height: 20px;
    font-size: 12px;
  }
}

.custom-badge {
  border-radius: 999px;
  padding: 2px 10px;
  font-size: 11px;
  font-weight: 700;

  &.badge-outline {
    border: 1px solid;
  }

  &.badge-blue {
    background-color: #E2EBF7;
    border-color: $blue;
    color: $blue;
  }

  &.badge-red {
    background-color: #FFC0C0;
    border-color: $red;
    color: $red;
  }

  &.badge-orange {
    background-color: #FFC0C0;
    border-color: $orange;
    color: $orange;
  }

  &.badge-green {
    background-color: #C0FFC0;
    border-color: $green;
    color: $green;
  }

  &.badge-purple {
    background-color: #C0C0FF;
    border-color: $purple;
    color: $purple;
  }
}

.status-badge {
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.025em;

  &.status-active {
    background-color: #dcfce7;
    color: #166534;
  }

  &.status-pending {
    background-color: #fef3c7;
    color: #92400e;
  }

  &.status-inactive {
    background-color: #f3f4f6;
    color: #6b7280;
  }
}

.premium-badge {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background-color: #fef3c7;
  color: #92400e;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
}

.submenu {
  margin-top: 2px;
  padding-left: 0;
}

.submenu-enter-active,
.submenu-leave-active {
  transition: height 0.2s ease-in-out;
  overflow: hidden;
}

.submenu-enter-from,
.submenu-leave-to {
  height: 0;
}
</style>
